<template>
  <div :class="{ section: !item }">
    <page-header :title="`Serienumre (${pagination.total})`">
      <button
        class="button is-default mr-2"
        @click="resetFilters"
        :disabled="!activeFilters"
      >
        Nultil filtre
      </button>
      <button
        v-if="!item"
        class="button is-default mr-2"
        @click="getTestList"
        :disabled="testListCount == 0"
      >
        Test / Rep ({{ testListCount }})
      </button>
      <button
        v-if="!item"
        class="button is-default mr-2"
        @click="getReturnList"
        :disabled="returnListCount == 0"
      >
        Returliste ({{ returnListCount }})
      </button>
      <button class="button is-default mr-2" @click="openItemList">
        Lager
      </button>
      <button class="button is-primary" @click="openModal(null)">
        Opret serienummer
      </button>
    </page-header>

    <div class="field">
      <input
        v-model="query"
        class="input mr-2"
        @keydown.tab.prevent="disableTab"
        placeholder="Søg efter model, serienummer, lokation, status, sagsnr., note..."
        type="search"
        @input="search()"
      />
    </div>
    <div class="field is-flex is-justify-content-space-between">
      <div class="is-flex filter-controls gap-2">
        <b-select v-model="statusId" @input="fetchSerials()">
          <option :value="0">Vis alle</option>
          <option
            v-for="status in statuses"
            :value="status.id"
            :key="status.id"
          >
            {{ status.name }}
          </option>
        </b-select>
        <b-select v-model="locationId" @input="fetchSerials()">
          <option :value="0">Vælg lokation</option>
          <option
            v-for="location in locations"
            :value="location.id"
            :key="location.id"
          >
            {{ location.full_name }}
          </option>
        </b-select>
        <b-select v-model="priceTypeId" @input="fetchSerials()">
          <option :value="0">Vælg pristype</option>
          <option
            v-for="priceType in priceTypes"
            :value="priceType.id"
            :key="priceType.id"
          >
            {{ priceType.name }}
          </option>
        </b-select>
      </div>
      <div>
        <b-pagination
          v-if="!item"
          class="is-hidden-touch is-hidden-desktop-only hide-dots"
          v-model="currentPage"
          :per-page="pagination.per_page"
          :total="pagination.total"
          @change="paginate"
          order="is-left"
        >
          <template v-slot:default> </template>
        </b-pagination>
      </div>
    </div>

    <table class="table is-fullwidth" ref="serialsList">
      <thead>
        <tr>
          <th @click="onUpdateOrderBy('items.model')" class="is-clickable">
            Model
            <SortArrow
              :orderBy="orderBy"
              field="items.model"
              :direction="direction"
            />
          </th>
          <th @click="onUpdateOrderBy('serial')" class="is-clickable">
            Serienummer
            <SortArrow
              :orderBy="orderBy"
              field="serial"
              :direction="direction"
            />
          </th>
          <th class="is-clickable">
            Sagsnr.
            <SortArrow
              :orderBy="orderBy"
              field="latest_assignment.project_id"
              :direction="direction"
            />
          </th>
          <th @click="onUpdateOrderBy('locations.name')" class="is-clickable">
            Lokation
            <SortArrow
              :orderBy="orderBy"
              field="locations.full_name"
              :direction="direction"
            />
          </th>
          <th @click="onUpdateOrderBy('note')" class="is-clickable">
            Note
            <SortArrow :orderBy="orderBy" field="note" :direction="direction" />
          </th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody v-if="!isFetching">
        <tr v-for="serial in serials" :key="serial.id">
          <td
            class="model"
            :class="{ pointer: !item }"
            @click="openItem(serial)"
          >
            {{ serial.item.model }}
          </td>
          <td
            class="serial"
            :class="{ pointer: !item }"
            @click="openItem(serial)"
            v-html="displaySerial(serial)"
          />
          <td
            class="project_id is-clickable"
            :class="{ pointer: !item }"
            @click="openProject(serial)"
            v-html="displayProjectId(serial)"
          />
          <td
            class="location"
            :class="{ pointer: !item }"
            @click="openItem(serial)"
            v-html="displayLocation(serial)"
          />
          <td
            class="note"
            :class="{ pointer: !item }"
            @click="openItem(serial)"
          >
            {{ serial.note }}
          </td>
          <td class="td-button">
            <a
              class="button is-small"
              title="Historik"
              @click.prevent="showHistory(serial)"
            >
              <span class="icon"><i class="fas fa-history" /></span>
            </a>
          </td>
          <td class="td-button">
            <a
              class="button is-small"
              title="Rediger"
              @click="openModal(serial)"
            >
              <span class="icon">
                <i class="fas fa-edit"></i>
              </span>
            </a>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr v-for="index in skeletonRows" :key="index">
          <td class="serial"><b-skeleton :animated="true"></b-skeleton></td>
          <td class="location"><b-skeleton :animated="true"></b-skeleton></td>
          <td class="status"><b-skeleton :animated="true"></b-skeleton></td>
          <td class="note"><b-skeleton :animated="true"></b-skeleton></td>
          <td>
            <span class="icon is-small">
              <b-skeleton></b-skeleton>
            </span>
          </td>
          <td>
            <span class="icon is-small">
              <b-skeleton></b-skeleton>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mt-2">
      <b-pagination
        v-model="currentPage"
        :per-page="pagination.per_page"
        :total="pagination.total"
        @change="paginate"
        order="is-right"
      >
      </b-pagination>
    </div>

    <SerialModal
      v-if="modalOpen"
      :active="modalOpen"
      :item="item"
      :serial="serial"
      :is-admin="isAdmin"
      :showAdminFields="showAdminFields"
      :locations="locations"
      :statuses="statuses"
      @closed="onCloseSerialModal"
      @updated="onSerialUpdated"
    />

    <SerialEventsModal
      v-if="showSerialEventsModal"
      :active="showSerialEventsModal"
      :serial="selectedSerial"
      @closed="onCloseEventsModal"
    />
  </div>
</template>

<script>
import PageHeader from "@elems/pageHeader";
import debounce from "lodash/debounce";
import SerialModal from "./forms/SerialModal";
import SerialEventsModal from "./show/SerialEventsModal";
import { mapGetters } from "vuex";
import SortArrow from "@elems/SortArrow";

export default {
  components: {
    PageHeader,
    SerialModal,
    SerialEventsModal,
    SortArrow
  },
  props: {
    item: { type: Object, required: false },
    showAdminFields: { type: Boolean, default: true }
  },
  data() {
    return {
      serials: [],
      pagination: { total: 0 },
      modalOpen: false,
      serial: null,
      query: "",
      currentPage: 1,
      isFetching: false,
      locationId: 0,
      statusId: 0,
      priceTypeId: 0,
      locations: [],
      statuses: [],
      priceTypes: [],
      activeFilters: false,
      showSerialEventsModal: false,
      selectedSerial: null,
      returnListCount: 0,
      testListCount: 0,
      skeletonRows: 20,
      orderBy: null,
      direction: null
    };
  },
  computed: {
    apiUrl() {
      return this.item ? `/api/items/${this.item.id}` : "/api";
    },
    ...mapGetters([
        'isAdmin',
        'listItemCategories'
      ])
  },
  methods: {
    disableTab(event){
    },
    toggleDirection() {
      this.direction = this.direction === "asc" ? "desc" : "asc";
    },
    onUpdateOrderBy(key) {
      this.toggleDirection();
      this.orderBy = key;
      this.fetchSerials();
    },
    openItemList() {
      this.$router.push({ name: "item.index" });
    },
    displaySerial(serial) {
      return serial.serial ? serial.serial : "<em>Intet serienummer</em>";
    },
    displayProjectId(serial) {
      return serial.latest_assignment && !serial.status.for_sale
        ? serial.latest_assignment.project_id
        : "<em>Intet sagsnummer</em>";
    },
    displayLocation(serial) {
      return serial.location ? serial.location.full_name : "<em>Ingen lokation</em>";
    },
    openItem(serial) {
      this.$router.push({
        name: "items",
        params: {
          id: serial.item_id
        },
        query: {
          q: serial.serial
        }
      });
    },
    openProject(serial) {
      if (serial.latest_assignment != null && !serial.status.for_sale) {
        this.$router.push(
          { 
            name: 'project.edit', 
            params: { id: serial.latest_assignment.project_id }
          }
        );
      }
    },
    openModal(serial) {
      if (serial) {
        window.axios
          .get(`/api/serials/${serial.id}`)
          .then(({ data }) => {
            this.serial = data;
            this.modalOpen = true;
          })
          .catch(error => {
            console.error(error);
          });
      } else {
        this.modalOpen = true;
      }
    },
    onCloseSerialModal() {
      this.modalOpen = false;
      this.serial = null;
    },
    onSerialUpdated() {
      this.$emit("updated");
      this.updateReturnListCount();
      this.updateTestRepListCount();
      this.fetchSerials();
    },
    paginate(value) {
      this.currentPage = value;
      this.fetchSerials();
    },
    search: debounce(function() {
      this.currentPage = 1;
      this.fetchSerials();
    }, 200),
    async fetchSerials(updateOptions = false) {
      this.isFetching = true;

      if (updateOptions) {
        await window.axios
          .get("/api/locations")
          .then(({ data }) => {
            this.locations = data;
          })
          .catch(error => {
            console.error(error);
          });

        await window.axios
          .get("/api/serial-statuses")
          .then(({ data }) => {
            this.statuses = data;
          })
          .catch(error => {
            console.error(error);
          });

        if (this.item) {
          this.priceTypes = this.item.prices.map(price => {
            return {
              id: price.type.id,
              name: price.type.name
            };
          });
        } else {
          await window.axios
            .get("/api/price-types")
            .then(({ data }) => {
              this.priceTypes = data;
            })
            .catch(error => {
              console.error(error);
            });
        }

        // Hack
        if (this.$route.name === "serials") {
          if (this.query === null && this.statusId === 0) {
            const inStock = this.statuses.filter(
              status => status.key === "in_stock"
            );
            if (inStock.length > 0) {
              this.statusId = inStock[0].id;
            }
          }
        }
      }

      let params = {};

      if (this.query) params["q"] = this.query;
      if (this.locationId) params["location"] = this.locationId;
      if (this.statusId) params["status"] = this.statusId;
      if (this.priceTypeId) params["price-type"] = this.priceTypeId;
      if (this.orderBy) params["order-by"] = this.orderBy;
      if (this.direction) params["direction"] = this.direction;

      this.activeFilters = Object.keys(params).length;

      if (this.currentPage > 1) params["page"] = this.currentPage;

      if (this.item)
        this.$router.push({ path: `/items/${this.item.id}`, query: params });
      else this.$router.push({ path: `/serials`, query: params });

      params["with"] = "location,status,events,item";

      if (!this.item) params["prPage"] = 20;

      window.axios
        .get(`${this.apiUrl}/serials`, { params })
        .then(({ data }) => {
          this.serials = data.data;
          this.pagination = data.meta;
          this.isFetching = false;
        })
        .catch(error => {
          console.error(error);
          this.isFetching = true;
        });
    },
    isId(value) {
      return /^[1-9]\d*$/.test(value);
    },
    resetFilters(fetch = true) {
      this.query = null;
      this.locationId = 0;
      this.statusId = 0;
      this.priceTypeId = 0;
      this.orderBy = null;
      this.direction = null;
      if (fetch) {
        this.fetchSerials();
      }
    },
    showHistory(serial) {
      this.selectedSerial = serial;
      this.$store.dispatch("loadSerialEvents", serial);
      this.showSerialEventsModal = true;
    },
    onCloseEventsModal() {
      this.showSerialEventsModal = false;
      this.selectedSerial = null;
    },
    getReturnList() {
      this.setStatusFilterByKey("on_return_list");
    },
    getTestList() {
      this.setStatusFilterByKey("test_rep");
    },
    setStatusFilterByKey(key) {
      this.resetFilters(false);
      const testStatus = this.statuses.filter(status => status.key === key);
      if (testStatus.length === 1) {
        this.statusId = testStatus[0].id;
        this.fetchSerials();
      }
    },
    updateReturnListCount() {
      if (this.item) return;

      window.axios
        .get("/api/serials/return-list")
        .then(data => {
          this.returnListCount = data.data;
        })
        .catch(error => {
          console.error(error);
        });
    },
    updateTestRepListCount() {
      if (this.item) return;

      window.axios
        .get("/api/serials/test-rep-list")
        .then(data => {
          this.testListCount = data.data;
        })
        .catch(error => {
          console.error(error);
        });
    },
    initScrollIntoListView() {
      if (!this.query || !this.$refs.serialsList) {
        return;
      }

      this.$refs.serialsList.scrollIntoView({
        behavior: "smooth"
      });
    }
  },
  mounted() {
    this.$store.dispatch('loadItemCategories');
    this.initScrollIntoListView();
  },
  created() {
    if (this.item) {
      this.skeletonRows = 5;
    }

    this.updateReturnListCount();
    this.updateTestRepListCount();

    let params = new URLSearchParams(window.location.search);

    this.query = params.get("q");
    const page = params.get("page");
    const locationId = params.get("location");
    const statusId = params.get("status");
    const priceTypeId = params.get("price-type");
    const orderBy = params.get("order-by");
    const direction = params.get("direction");

    this.currentPage = page && this.isId(page) ? parseInt(page) : 1;
    this.locationId =
      locationId && this.isId(locationId) ? parseInt(locationId) : 0;
    this.statusId = statusId && this.isId(statusId) ? parseInt(statusId) : 0;
    this.priceTypeId =
      priceTypeId && this.isId(priceTypeId) ? parseInt(priceTypeId) : 0;

    this.orderBy = orderBy || null;
    this.direction = direction || null;

    this.fetchSerials(true);
  }
};
</script>

<style scoped lang="scss">
.location {
  width: 15%;
}
.model {
  width: 15%;
}
.serial {
  width: 15%;
}
.project_id {
  width: 15%;
}
.note {
  width: 40%;
}
.td-button {
  padding: 0.5rem 0 0.5rem 0;
}
.pointer {
  cursor: pointer;
}
</style>

<style>
.hide-dots .pagination-list {
  display: none;
}
.filter-controls select {
  width: 180px;
}
</style>
